/**
 * Toaster — sonner mounted once at the app root, themed to the radical paper
 * shell. The `toast` re-export is the consumer-facing API; call `toast.ok(…)`,
 * `toast.warn(…)`, or `toast.error(…)` from anywhere.
 *
 * Sharp corners + hairline border match the rest of the system; the toast
 * itself uses `bg-paper` so it reads cleanly on both `--bg` shades. Tone
 * variants flip a left accent bar on instead of recoloring the whole card.
 *
 * Phase 12.2 motion language: the entrance/exit animation lives inside sonner
 * (CSS keyframes — `[data-sonner-toast]` translates from the screen edge +
 * fades), and the timing curve is overridden in [globals.css](../styles/globals.css)
 * to match the rest of the new motion vocabulary (cubic-bezier(0.2, 0, 0.13, 1)
 * over 240 ms — same curve + duration as the Savebar slide and modal scrim).
 * `prefers-reduced-motion` zero-durations these via the global guard in
 * globals.css. We intentionally don't migrate sonner to framer-motion — sonner
 * already manages the stack + swipe-to-dismiss + auto-expire lifecycle, and
 * tuning its CSS variables is enough to feel coherent with the rest.
 *
 * Extrapolated within the design language — no dedicated artboard.
 */

import { Toaster as SonnerToaster, toast as sonnerToast } from 'sonner';
import type { ReactNode } from 'react';
import { useTheme } from '@/lib/theme';
import { useBreakpoint } from '@/lib/useBreakpoint';

interface ToasterProps {
  position?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';
  /** Default visible time, in ms. */
  duration?: number;
}

export function Toaster({ position = 'bottom-right', duration = 4500 }: ToasterProps) {
  const { resolved } = useTheme();
  // Phone (14.7): toasts move to the top — at 390px a bottom-right stack sits
  // exactly on the sticky Savebar / detail CTAs and intercepts taps on the
  // primary action for its whole visible duration. Position is a sonner JS
  // prop, so this is a hook swap, not CSS.
  const { isPhone } = useBreakpoint();
  return (
    <SonnerToaster
      theme={resolved}
      // Toasts are screen-only feedback: a save toast ("Protokol vytvořen.")
      // can still be visible when the user hits print on a protocol sheet and
      // would end up on the printed page.
      className="print:hidden"
      position={isPhone ? 'top-center' : position}
      duration={duration}
      gap={8}
      offset={24}
      visibleToasts={5}
      toastOptions={{
        unstyled: true,
        classNames: {
          toast:
            'group/toast w-full border border-line bg-paper text-ink shadow-[0_12px_32px_rgba(0,0,0,0.18)] flex items-start gap-3 px-3.5 py-3 font-sans text-[13px] leading-snug',
          title: 'font-medium text-ink',
          description: 'mt-0.5 text-ink-2',
          actionButton:
            'inline-flex items-center px-2 py-1 border border-ink bg-ink text-paper font-mono text-[10.5px] uppercase tracking-[0.06em] hover:bg-accent hover:border-accent',
          cancelButton:
            'inline-flex items-center px-2 py-1 border border-line bg-paper text-ink-2 font-mono text-[10.5px] uppercase tracking-[0.06em] hover:border-ink hover:text-ink',
          success: 'border-l-[3px] border-l-ok',
          error: 'border-l-[3px] border-l-warn',
          warning: 'border-l-[3px] border-l-warn',
          info: 'border-l-[3px] border-l-accent',
          closeButton:
            'border border-line bg-paper text-ink-2 hover:border-ink hover:text-ink',
        },
      }}
    />
  );
}

/**
 * `toast` is sonner's full API; `ok` / `warn` are thin aliases that map to the
 * design's semantic palette so consumers don't have to remember whether
 * "warn" or "warning" is the sonner key.
 */
export const toast = Object.assign(sonnerToast, {
  ok: (message: ReactNode, opts?: Parameters<typeof sonnerToast.success>[1]) =>
    sonnerToast.success(message, opts),
  warn: (message: ReactNode, opts?: Parameters<typeof sonnerToast.warning>[1]) =>
    sonnerToast.warning(message, opts),
});
